// src/pages/admin/InactiveMember.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search, 
  UserX, 
  Phone, 
  Mail,
  Lock,
  Download,
  User,
  Filter,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  LogIn,
  Users,
  Unlock,
  DollarSign,
  TrendingUp,
  CreditCard,
  Globe,
  Calendar,
  RefreshCw,
  Eye,
  Key,
  Package
} from 'lucide-react';
import { database, ref, onValue, update } from '../../firebaseConfig';

const InactiveMember = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedMobileMember, setExpandedMobileMember] = useState(null);
  const [showPassword, setShowPassword] = useState({});

  const [filters, setFilters] = useState({
    name: '',
    sponsorId: '',
    mobile: '',
    email: '',
    country: ''
  });

  // Fetch all users from Firebase
  useEffect(() => {
    const usersRef = ref(database, 'users');
    
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      const usersList = [];
      
      if (data) {
        // Iterate through date folders
        Object.keys(data).forEach(dateKey => {
          const dateData = data[dateKey];
          
          if (dateData && typeof dateData === 'object') {
            // Iterate through users under each date
            Object.keys(dateData).forEach(userKey => {
              const user = dateData[userKey];
              
              // Skip admin and invalid entries
              if (!user || userKey === 'admin' || user.userId === 'admin' || user.role === 'admin') {
                return;
              }
              
              usersList.push({
                id: `${dateKey}_${userKey}`,
                firebaseDateKey: dateKey,
                firebaseUserKey: userKey,
                ...user
              });
            });
          }
        });
      }
      
      setAllUsers(usersList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching users:", error);
      setLoading(false);
    });
    
    return () => {
      unsubscribe();
    };
  }, []);

  // Identify inactive members: those WITHOUT any package OR with status "inactive"
  const inactiveMembers = useMemo(() => {
    return allUsers.filter(user => {
      // Check if user has any package
      const hasPackage = user.packages && Array.isArray(user.packages) && user.packages.length > 0;
      const hasSinglePackage = user.package && Object.keys(user.package).length > 0;
      
      // User is inactive if:
      // 1. Has no packages AND status is not 'active' (or undefined)
      // 2. Has status explicitly set to 'inactive'
      if (user.status === 'inactive') return true;
      if (!hasPackage && !hasSinglePackage) return true;
      return false;
    });
  }, [allUsers]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Filter members based on search
  const filteredMembers = useMemo(() => {
    return inactiveMembers.filter(member => {
      const textMatches = (text, searchTerm) => {
        if (!searchTerm) return true;
        if (!text) return false;
        return text.toString().toLowerCase().includes(searchTerm.toLowerCase());
      };

      return (
        textMatches(member.name || '', filters.name) &&
        textMatches(member.sponsorId || '', filters.sponsorId) &&
        textMatches(member.mobile || '', filters.mobile) &&
        textMatches(member.email || '', filters.email) &&
        textMatches(member.country || '', filters.country)
      );
    });
  }, [inactiveMembers, filters]);

  const togglePasswordVisibility = (id, e) => {
    e?.stopPropagation();
    setShowPassword(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const activateMember = async (member) => {
    if (!window.confirm(`Activate ${member.name || 'this member'}?`)) return;
    
    try {
      const memberRef = ref(database, `users/${member.firebaseDateKey}/${member.firebaseUserKey}`);
      await update(memberRef, { status: 'active' });
      
      // Update local state
      setAllUsers(prev => prev.map(user => 
        user.id === member.id ? { ...user, status: 'active' } : user
      ));
      
      alert('Member activated successfully!');
    } catch (error) {
      console.error("Activation error:", error);
      alert("Failed to activate member");
    }
  };

  const autoLoginAsUser = (userData) => {
    localStorage.setItem('autoLoginUserId', userData.userId || userData.firebaseUserKey);
    localStorage.setItem('autoLoginUserName', userData.name || 'User');
    localStorage.setItem('autoLoginUserData', JSON.stringify(userData));
    
    const loginToken = `auto_${Date.now()}_${Math.random().toString(36).substr(2)}`;
    localStorage.setItem('autoLoginToken', loginToken);
    
    window.open(`/user-dashboard?autoLogin=true&token=${loginToken}`, '_blank');
  };

  const exportToCSV = () => {
    const headers = ['Sr.No', 'Member ID', 'Name', 'Sponsor ID', 'Mobile', 'Email', 'Country', 'Status', 'Balance', 'Joining Date'];
    
    const csvData = filteredMembers.map((member, index) => {
      const memberId = member.memberId || member.userId || `AV${String(member.firebaseUserKey).substring(0, 6)}`;
      
      return [
        index + 1,
        memberId,
        member.name || 'Unknown',
        member.sponsorId || 'N/A',
        member.mobile || 'N/A',
        member.email || 'N/A',
        member.country || 'N/A',
        member.status || 'inactive',
        member.balance || 0,
        formatDate(member.registrationDate || member.firebaseDateKey)
      ];
    });
    
    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inactive-members_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  const toggleRowExpand = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const toggleMobileMember = (id) => {
    setExpandedMobileMember(expandedMobileMember === id ? null : id);
  };

  // Desktop Table Row Component
  const DesktopTableRow = ({ member, index }) => {
    const memberId = member.memberId || member.userId || `AV${String(member.firebaseUserKey).substring(0, 6)}`;
    const hasPackage = (member.packages && member.packages.length > 0) || member.package;
    
    return (
      <React.Fragment key={member.id}>
        <tr className="hover:bg-secondary/10 transition-colors">
          <td className="py-2 px-1 text-center text-muted-foreground text-xs">
            {index + 1}
          </td>
          
          <td className="py-2 px-1">
            <div className="flex items-center">
              <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center mr-2">
                <User className="h-3 w-3 text-red-400" />
              </div>
              <div>
                <div className="font-medium text-foreground text-xs truncate max-w-[80px]">
                  {member.name || 'Unknown'}
                </div>
                <div className="text-muted-foreground text-[10px]">
                  ID: {memberId}
                </div>
              </div>
            </div>
          </td>

          <td className="py-2 px-1">
            <div className="text-foreground text-xs truncate max-w-[70px]">
              {member.sponsorId || 'N/A'}
            </div>
          </td>

          <td className="py-2 px-1">
            <div className="text-foreground text-xs truncate max-w-[90px]">
              {member.mobile || 'N/A'}
            </div>
          </td>

          <td className="py-2 px-1">
            <div className={`inline-flex items-center px-1 py-0.5 rounded text-xs ${
              hasPackage 
                ? 'bg-orange-500/20 border border-orange-500/30' 
                : 'bg-red-500/20 border border-red-500/30'
            }`}>
              <Package className="h-2 w-2 mr-1" />
              <div className="text-[10px]">
                {hasPackage ? 'Has Package' : 'No Package'}
              </div>
            </div>
          </td>

          <td className="py-2 px-1">
            <div className="text-foreground text-xs">
              ${(member.balance || 0).toFixed(0)}
            </div>
          </td>

          <td className="py-2 px-1">
            <button
              onClick={() => autoLoginAsUser(member)}
              className="flex items-center gap-1 px-2 py-1 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-colors text-[10px] whitespace-nowrap"
              title="Login as User"
            >
              <LogIn className="h-3 w-3" />
              <span>Login</span>
            </button>
          </td>
        </tr>
        
        {/* Expanded Details */}
        {expandedRow === member.id && (
          <tr className="bg-secondary/5">
            <td colSpan="7" className="py-3 px-3">
              <div className="bg-secondary/20 border border-border rounded-lg p-3">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">Email</div>
                    <div className="text-foreground truncate">{member.email || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">Country</div>
                    <div className="text-foreground">{member.country || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">Password</div>
                    <div className="flex items-center">
                      <span className="font-mono text-foreground">
                        {showPassword[member.id] ? (member.password || 'N/A') : '••••••'}
                      </span>
                      <button
                        onClick={(e) => togglePasswordVisibility(member.id, e)}
                        className="ml-2 hover:text-primary"
                      >
                        {showPassword[member.id] ? (
                          <Lock className="h-3 w-3" />
                        ) : (
                          <Key className="h-3 w-3" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">Join Date</div>
                    <div className="text-foreground">{formatDate(member.registrationDate)}</div>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Mobile Accordion Item Component
  const MobileAccordionItem = ({ member, index }) => {
    const memberId = member.memberId || member.userId || `AV${String(member.firebaseUserKey).substring(0, 6)}`;
    const hasPackage = (member.packages && member.packages.length > 0) || member.package;
    
    return (
      <div key={member.id} className="mb-4 overflow-hidden border border-border rounded-lg">
        <div 
          className="p-4 bg-secondary cursor-pointer flex justify-between items-center"
          onClick={() => toggleMobileMember(member.id)}
        >
          <div className="flex items-center gap-3">
            <div className="text-muted-foreground text-sm">#{index + 1}</div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
                <User className="h-4 w-4 text-red-400" />
              </div>
              <div>
                <div className="font-bold text-foreground text-sm">{member.name || 'Unknown'}</div>
                <div className="text-xs text-muted-foreground">
                  ID: {memberId}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${
              expandedMobileMember === member.id ? 'rotate-180' : ''
            }`} />
          </div>
        </div>
        
        {expandedMobileMember === member.id && (
          <div className="p-4 border-t border-border">
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-secondary/50 p-3 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Sponsor ID</div>
                <div className="text-sm text-foreground">{member.sponsorId || 'N/A'}</div>
              </div>
              <div className="bg-secondary/50 p-3 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Package</div>
                <div className={`text-sm ${hasPackage ? 'text-orange-400' : 'text-red-400'}`}>
                  {hasPackage ? 'Has Package' : 'No Package'}
                </div>
              </div>
              <div className="bg-secondary/50 p-3 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Mobile</div>
                <div className="text-sm text-foreground">{member.mobile || 'N/A'}</div>
              </div>
              <div className="bg-secondary/50 p-3 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Balance</div>
                <div className="text-sm text-foreground">${member.balance || 0}</div>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between">
                <span className="text-xs text-muted-foreground">Email:</span>
                <span className="text-xs text-foreground truncate max-w-[150px]">{member.email || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-muted-foreground">Country:</span>
                <span className="text-xs text-foreground">{member.country || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Password:</span>
                <div className="flex items-center">
                  <span className="font-mono text-xs mr-2">
                    {showPassword[member.id] ? (member.password || 'N/A') : '••••••'}
                  </span>
                  <button
                    onClick={(e) => togglePasswordVisibility(member.id, e)}
                    className="hover:text-primary"
                  >
                    {showPassword[member.id] ? (
                      <Lock className="h-3 w-3" />
                    ) : (
                      <Key className="h-3 w-3" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <button
                onClick={() => autoLoginAsUser(member)}
                className="w-full px-4 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
              >
                <LogIn className="h-4 w-4" />
                <span>Login as User</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-full mx-auto px-4">
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading inactive members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Inactive Members</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Members without packages or with status "inactive" • Total: {inactiveMembers.length}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-secondary/30 border border-border rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Filter className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold text-foreground">Filter</h2>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setFilters({ name: '', sponsorId: '', mobile: '', email: '', country: '' })}
              className="px-4 py-2 bg-secondary/20 hover:bg-secondary/30 text-muted-foreground rounded-lg transition-colors border border-border text-sm"
            >
              Clear
            </button>
            <button
              onClick={exportToCSV}
              disabled={filteredMembers.length === 0}
              className={`px-4 py-2 rounded-lg transition-all text-sm ${
                filteredMembers.length === 0
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'btn-electric'
              }`}
            >
              <Download className="h-4 w-4 mr-2 inline" />
              Export
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-muted-foreground mb-1 text-xs">Name:</label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <input
                type="text"
                name="name"
                value={filters.name}
                onChange={handleFilterChange}
                placeholder="Name"
                className="w-full pl-7 pr-2 py-1.5 bg-secondary/50 border border-border rounded-lg text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-muted-foreground mb-1 text-xs">Sponsor ID:</label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <input
                type="text"
                name="sponsorId"
                value={filters.sponsorId}
                onChange={handleFilterChange}
                placeholder="Sponsor ID"
                className="w-full pl-7 pr-2 py-1.5 bg-secondary/50 border border-border rounded-lg text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-muted-foreground mb-1 text-xs">Mobile:</label>
            <div className="relative">
              <Phone className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <input
                type="text"
                name="mobile"
                value={filters.mobile}
                onChange={handleFilterChange}
                placeholder="Mobile"
                className="w-full pl-7 pr-2 py-1.5 bg-secondary/50 border border-border rounded-lg text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-muted-foreground mb-1 text-xs">Country:</label>
            <div className="relative">
              <Globe className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <input
                type="text"
                name="country"
                value={filters.country}
                onChange={handleFilterChange}
                placeholder="Country"
                className="w-full pl-7 pr-2 py-1.5 bg-secondary/50 border border-border rounded-lg text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-hidden border border-border rounded-xl mb-8">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary/50">
              <tr className="text-xs">
                <th className="py-2 px-1 text-center w-12">#</th>
                <th className="py-2 px-1 text-left">Member</th>
                <th className="py-2 px-1 text-left">Sponsor ID</th>
                <th className="py-2 px-1 text-left">Mobile</th>
                <th className="py-2 px-1 text-left">Package Status</th>
                <th className="py-2 px-1 text-left">Balance</th>
                <th className="py-2 px-1 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {filteredMembers.length > 0 ? (
                filteredMembers.map((member, index) => (
                  <DesktopTableRow key={member.id} member={member} index={index} />
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="py-16 text-center">
                    <div className="text-muted-foreground text-6xl mb-4">👤</div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      {inactiveMembers.length === 0 ? 'No Inactive Members!' : 'No Members Found'}
                    </h3>
                    <p className="text-muted-foreground">
                      {inactiveMembers.length === 0 
                        ? 'All members have packages or are active.' 
                        : 'Try adjusting your search filters'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Accordion View */}
      <div className="md:hidden">
        {filteredMembers.length > 0 ? (
          filteredMembers.map((member, index) => (
            <MobileAccordionItem key={member.id} member={member} index={index} />
          ))
        ) : (
          <div className="p-8 text-center">
            <div className="text-muted-foreground text-6xl mb-4">👤</div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              {inactiveMembers.length === 0 ? 'No Inactive Members!' : 'No Members Found'}
            </h3>
            <p className="text-muted-foreground">
              {inactiveMembers.length === 0 
                ? 'All members have packages or are active.' 
                : 'Try adjusting your search filters'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InactiveMember;
